import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

import useTimekeepingCtx, {
  TimekeepingContext,
} from "../../context/domain/timekeeping/TimekeepingContext";
import { getValidDateFromUrl } from "../../utils/datetime";

import ClockIn from "../../features/timekeeping/workerClock/ClockIn";
import ClockOut from "../../features/timekeeping/workerClock/ClockOut";

import ShiftDateDisplay from "../../features/timekeeping/workerClock/infoBoxes/ShiftDateDisplay";
import ShiftSummary from "../../features/timekeeping/workerClock/infoBoxes/ShiftSummary";

import { Button } from "../../components/ui";

/**
 * WorkerClock Page Component
 *
 * Serves as the main interface for user clock in functionality, allowing
 * employees to clock in, clock out, and view their shift summary information.
 *
 * Features:
 * - Provides context for all child timekeeping components via TimekeepingContext
 * - Dynamically renders different components based on user's shift status
 * - Displays the current shift date and handles date from URL parameters
 * - Shows ClockIn component when user is not clocked in
 * - Shows ClockOut component when user is clocked in
 * - Displays shift summary for all shifts in the current day
 * - Allows adding multiple shifts in a single day through workflow
 * - Provides navigation to the shift schedule page for a more detailed view
 *
 * State Management:
 * - Uses TimekeepingContext to manage and share timekeeping state across components
 * - Tracks whether user is clocked in or has completed their shift
 * - Manages local dayComplete state to control adding multiple shifts
 * - Handles loading states for asynchronous operations
 *
 * URL Integration:
 * - Supports date specification via URL parameters
 * - Falls back to current date in timekeeping context when URL date is invalid or not
 *   provided
 *
 * @returns {JSX.Element} The rendered WorkerClock page UI
 */
const WorkerClock = () => {
  const { date: urlDate } = useParams();
  // Get a valid date from the URL or default to today via the context
  const validDate = getValidDateFromUrl(urlDate);
  const timekeepingCtx = useTimekeepingCtx(validDate);
  const { weeklyShiftData, date, isLoading, clearMessage } = timekeepingCtx;
  const navigate = useNavigate();

  // Allow clocking in for multiple shifts in a day for a single workflow
  // Once outside this workflow, user must add shifts through edit in the shift schedule
  // Hence this state is local to this component
  const [dayComplete, setDayComplete] = useState(null);

  // Get shift data for the current date (all shifts for that day)
  // Filter out shifts with stage "Current Shift" or "No Shift"
  // This is becuase they are not relevant for clocking in.
  const shiftData = (
    weeklyShiftData?.days.find((day) => day.date === date)?.shifts || []
  ).filter(
    (shift) =>
      shift.stage !== "Current Shift" &&
      shift.stage !== "No Shift" &&
      shift.stage !== "Missed Shift"
  );
  const mostRecentShiftStage =
    shiftData[shiftData.length - 1]?.stage || "No Shift Data";

  // Check if the user is clocked in by looking at the last shift's stage
  // This is used to determine if the ClockOut component should be shown
  const isClockedIn =
    shiftData.length > 0 && mostRecentShiftStage === "Clocked In";
  const noShiftsYet = shiftData.length === 0;

  document.title = "R&S Clock In";

  useEffect(() => {
    // Here we decide if the user is able to clock in or not
    if (noShiftsYet || isClockedIn) {
      setDayComplete(false);
    } else {
      setDayComplete(true);
    }
  }, [noShiftsYet, isClockedIn]);

  return (
    <TimekeepingContext.Provider value={timekeepingCtx}>
      <div className="p-2 pb-0 max-w-[1024px] mx-auto">
        <div className="bg-white rounded-lg shadow-md p-2 mb-6">
          {/* Always show the date for UX reasons */}
          <ShiftDateDisplay />{" "}
          {/* Display shift summary data if there is any */}
          {shiftData && shiftData.length > 0 && (
            <div className="bg-gray-100 font-inter caret-transparent max-w-[1024px] mx-auto">
              {shiftData.map((shift, index) => (
                <ShiftSummary
                  key={index}
                  shift={shift}
                  className={shiftData.length > 0 ? "mt-2" : ""}
                />
              ))}
            </div>
          )}
          {/* Show ClockIn component if user is not clocked in or has multiple shifts */}
          {!dayComplete && !isClockedIn && (
            <div className={shiftData.length > 0 ? "mt-2" : ""}>
              <ClockIn />
            </div>
          )}
          {/* Show ClockOut component if user is clocked in */}
          {isClockedIn && (
            <ClockOut
              startVisible
              date={date}
              setDayComplete={setDayComplete}
            />
          )}
        </div>

        {/* Button to navigate to the shift schedule page */}
        <Button
          onClick={() => {
            navigate(`/timekeeping/worker-schedule/${date}`);
            clearMessage();
          }}
          variant="primary"
          text="View Shift Schedule"
          icon={null}
          className="py-3 px-6 my-4"
        />
        {mostRecentShiftStage === "Complete Shift" && (
          <Button
            onClick={() => {
              setDayComplete(false); // Set state to allow clocking in again
            }}
            variant={isLoading ? "disabled" : "custom"}
            text={isLoading ? "" : "Add Another Shift"}
            icon={isLoading ? "spinner" : "plus"}
            className="py-1 px-6 mt-8 sm:mt-8 w-full bg-white hover:bg-gray-300 text-gray-900"
          />
        )}
      </div>
    </TimekeepingContext.Provider>
  );
};

export default WorkerClock;
